import * as React from 'react'
import { useIsLoggedInQuery, useUserAuthQuery } from 'auth/hooks'
import type {
  EventJson,
  NotificationPayloadJson,
} from '../gen/creativefabrica/notifications/v1/types_pb'
import { subscribeToEvent } from './notifications'
import { streamUsesCookieAuth } from './sse-connection'

export type NotificationPayload = NotificationPayloadJson

interface UseNotificationsOptions {
  event?: EventJson
  onNotification?: (notification: NotificationPayloadJson) => void
  /** See `subscribeToEvent`: fired when coverage begins, on registration and after reconnects. */
  onSubscribed?: () => void
  enabled?: boolean
}

export function useNotifications({
  event,
  onNotification,
  onSubscribed,
  enabled = true,
}: UseNotificationsOptions) {
  const [currentNotification, setCurrentNotification] = React.useState<
    NotificationPayloadJson | undefined
  >(undefined)
  const hasBeenAccessedRef = React.useRef(false)
  const onNotificationRef = React.useRef<typeof onNotification>(onNotification)
  const onSubscribedRef = React.useRef<typeof onSubscribed>(onSubscribed)
  const { data: token } = useUserAuthQuery(undefined, {
    select: data => data?.me?.token,
  })
  const { data: isLoggedIn } = useIsLoggedInQuery()
  // On a cookie host the stream is one per session and the workspace token is irrelevant to it,
  // so it must not be an effect dependency there.
  const cookieAuth = streamUsesCookieAuth()
  const streamToken = cookieAuth ? undefined : (token ?? undefined)
  const canSubscribe = cookieAuth ? Boolean(isLoggedIn) : Boolean(streamToken)

  React.useEffect(() => {
    onNotificationRef.current = onNotification
    onSubscribedRef.current = onSubscribed
  }, [onNotification, onSubscribed])

  React.useEffect(() => {
    if (!event || !enabled || !canSubscribe) {
      return
    }

    return subscribeToEvent(
      event,
      notification => {
        onNotificationRef.current?.(notification)
        if (hasBeenAccessedRef.current) {
          setCurrentNotification(notification)
        }
      },
      streamToken,
      { onSubscribed: () => onSubscribedRef.current?.() },
    )
  }, [event, streamToken, enabled, canSubscribe])

  // we can smortly see if the dev/user has accessed `.notification`,
  // if they have, we can then update the state. otherwise we don't need to.
  // this saves us from rerendering the component when the notification is updated if it's not used!
  const returnValue = React.useMemo(() => {
    return new Proxy<{ notification: NotificationPayloadJson | undefined }>(
      { notification: undefined },
      {
        get(target, prop) {
          if (prop === 'notification') {
            hasBeenAccessedRef.current = true
            return currentNotification
          }
          return target[prop as keyof typeof target]
        },
      },
    )
  }, [currentNotification])

  return returnValue
}
